/**
 * EFFET : webcam + MediaPipe Face Landmarker → FaceSource.
 * Analyse la vidéo BRUTE locale (jamais une image filtrée). Les landmarks
 * bruts sont conservés dans chaque frame (placement AR, V2 miroir déformant).
 * MediaPipe tourne dans un Web Worker quand c'est possible (`detector`) : le fil principal
 * garde l'interface, la visio et le miroir. Une image à la fois ; pendant l'analyse, les
 * autres sont sautées, et la DERNIÈRE sautée est analysée dès que le résultat revient, sans
 * attendre l'image caméra suivante (cadence ≈ 1000 / durée d'analyse, pas un multiple de
 * l'intervalle caméra).
 * Ne s'arrête jamais tout seul : si MediaPipe plante plusieurs fois de suite, ou ne répond
 * plus (worker gelé), il est rechargé.
 */
import { CAMERA_CONSTRAINTS } from './cameraConstraints';
import { createFrameStats, type FrameStatsSnapshot } from '../lib/frameStats';
import { TimeoutError, withTimeout } from '../lib/timeout';
import type { FaceFrameListener, FaceSource } from './faceSource';
import {
  createFrameCanvas,
  onCameraFrames,
  readPixels,
  type CameraFrames,
  type CameraImage,
} from './cameraFrames';
import { DEFAULT_DELEGATE, type Delegate } from './delegate';
import { createDetector, type Detector } from './detector';
import type { FrameLoopState } from './frameLoop';
import { describeError } from './landmarkerProtocol';
import { toFaceFrame } from './mediapipeMapping';

/** Échecs consécutifs de l'analyse avant de recharger MediaPipe. */
const MAX_CONSECUTIVE_FAILURES = 3;
/** Une analyse sans réponse au-delà : MediaPipe est rechargé sur-le-champ. */
export const DETECT_TIMEOUT_MS = 3000;

export interface FaceSourceHealth {
  /** Où MediaPipe calcule. */
  readonly delegate: Delegate;
  /** Worker ou fil principal ; `null` : pas chargé. */
  readonly where: 'worker' | 'main' | null;
  /** Boucle d'images (`null` : pas démarrée). */
  readonly loop: FrameLoopState | null;
  /** Analyses lancées / terminées (résultat reçu). */
  readonly started: number;
  readonly completed: number;
  /** Analyses restées sans réponse (worker gelé) : MediaPipe rechargé à chaque fois. */
  readonly timeouts: number;
  /** Appels de MediaPipe qui ont échoué (image ignorée), délais compris. */
  readonly analysisErrors: number;
  /** Rechargements de MediaPipe après des échecs consécutifs ou un délai dépassé. */
  readonly restarts: number;
  /** Message du dernier échec de MediaPipe ; `null` si aucun. */
  readonly lastError: string | null;
}

export interface FaceSourceStats extends FrameStatsSnapshot {
  /** Durée moyenne de l'inférence MediaPipe seule (ms) ; le reste d'`avgMs`, c'est le transport. */
  readonly inferenceMs: number;
}

export interface CameraFaceSource extends FaceSource {
  /** Cadence d'analyse et durée d'une analyse, aller-retour compris (fenêtre glissante d'1 s). */
  stats(): FaceSourceStats;
  health(): FaceSourceHealth;
  /** Arrête ET décharge MediaPipe (worker compris). `stop` garde le modèle pour repartir vite. */
  close(): void;
}

export interface CameraFaceSourceOptions {
  /** Élément vidéo à analyser ; son flux est fourni par l'appelant (déjà partagé avec WebRTC). */
  readonly video: HTMLVideoElement;
  /** Cadence d'analyse maximale, relue à chaque image (0 = en pause). Défaut : chaque image. */
  readonly maxFps?: () => number;
  /** Où MediaPipe calcule (défaut : GPU). */
  readonly delegate?: Delegate;
}

export { describeError };

export function createCameraFaceSource({
  video,
  maxFps,
  delegate = DEFAULT_DELEGATE,
}: CameraFaceSourceOptions): CameraFaceSource {
  const listeners = new Set<FaceFrameListener>();
  const frameStats = createFrameStats();
  const inferenceStats = createFrameStats();
  let detector: Detector<'face'> | undefined;
  let loop: CameraFrames | undefined;
  let starting: Promise<void> | undefined;
  let running = false;
  /** Incrémenté à chaque arrêt : un chargement du modèle périmé ne démarre pas de boucle. */
  let generation = 0;
  let lastAnalysis = -Infinity;
  let busy = false;
  /** Dernière image arrivée pendant une analyse : analysée dès que celle-ci se termine. */
  let pending: { image: CameraImage; t: number } | null = null;
  let consecutiveFailures = 0;
  let started = 0;
  let completed = 0;
  let timeouts = 0;
  let analysisErrors = 0;
  let restarts = 0;
  let lastError: string | null = null;
  let frameCanvas: ReturnType<typeof createFrameCanvas> | undefined;

  const rebuild = (cause: unknown): void => {
    const old = detector;
    detector = undefined;
    restarts++;
    consecutiveFailures = 0;
    console.warn('MediaPipe rechargé :', cause);
    old?.close();
    void createDetector('face', delegate)
      .then((fresh) => {
        if (running) detector = fresh;
        else fresh.close();
      })
      .catch((e: unknown) => {
        console.warn('Rechargement de MediaPipe impossible :', e);
      });
  };

  const onFailure = (e: unknown): void => {
    analysisErrors++;
    lastError = describeError(e);
    if (e instanceof TimeoutError) {
      timeouts++;
      rebuild(e);
    } else if (++consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) rebuild(e);
  };

  const onFrame = (image: CameraImage, t: number): void => {
    const current = detector;
    if (!current) return;
    if (busy) {
      pending = { image, t };
      return;
    }
    const cap = maxFps?.() ?? Infinity;
    if (!(cap > 0) || t - lastAnalysis < 1000 / cap - 2) return;
    const data = readPixels(image);
    if (!data) return;
    lastAnalysis = t;
    busy = true;
    started++;
    const before = performance.now();
    withTimeout(
      current.detect(data.data.buffer, data.width, data.height, t),
      DETECT_TIMEOUT_MS,
      'analyse du visage',
    ).then(
      ({ result, pixels, inferenceMs }) => {
        busy = false;
        if (detector !== current) return; // Rechargé ou fermé entre-temps.
        completed++;
        consecutiveFailures = 0;
        frameStats.record(t, performance.now() - before);
        inferenceStats.record(t, inferenceMs);
        frameCanvas ??= createFrameCanvas();
        const frame = {
          ...toFaceFrame(t, result),
          image: frameCanvas(pixels, data.width, data.height),
        };
        for (const l of listeners) l(frame);
        resumePending();
      },
      (e: unknown) => {
        busy = false;
        if (detector === current) onFailure(e);
        resumePending();
      },
    );
  };

  /** Une image est arrivée pendant l'analyse : on enchaîne sans attendre la suivante. */
  const resumePending = (): void => {
    const next = pending;
    pending = null;
    if (next && detector) onFrame(next.image, next.t);
  };

  const release = (): void => {
    detector?.close();
    detector = undefined;
    busy = false;
    pending = null;
  };

  return {
    start() {
      running = true;
      const current = generation;
      starting ??= (async () => {
        detector ??= await createDetector('face', delegate);
        if (current === generation) loop = onCameraFrames(video, onFrame);
      })().catch((e: unknown) => {
        starting = undefined;
        throw e;
      });
      return starting;
    },
    stop() {
      running = false;
      generation++;
      loop?.stop();
      loop = undefined;
      starting = undefined;
    },
    close() {
      this.stop();
      release();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    stats: () => ({ ...frameStats.get(), inferenceMs: inferenceStats.get().avgMs }),
    health: () => ({
      delegate,
      where: detector?.where ?? null,
      loop: loop?.state() ?? null,
      started,
      completed,
      timeouts,
      analysisErrors,
      restarts,
      lastError,
    }),
  };
}

/** Demande la webcam (jamais le micro). Messages d'erreur lisibles. */
export async function openCamera(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
  } catch (e) {
    const name = e instanceof DOMException ? e.name : '';
    if (name === 'NotAllowedError') throw new Error('Accès à la caméra refusé.', { cause: e });
    if (name === 'NotFoundError') throw new Error('Aucune caméra trouvée.', { cause: e });
    if (name === 'NotReadableError')
      throw new Error('Caméra déjà utilisée par une autre application.', { cause: e });
    throw e;
  }
}
